import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import React, { useEffect } from 'react'
import { NewUserRegistrationModal } from '../../components/modals/NewUserRegistrationModal'
import { useAuth } from '../../contexts/AuthContext'
import { AnimatedBackground } from '../../components/common/AnimatedBackground'
import { useQueryClient } from '@tanstack/react-query'
import { useRegistrationStatus } from '../../hooks/useRegistrationStatus'
import { queryKeys } from '../../services/query/queryKeys'

export const Route = createFileRoute('/register')({
  beforeLoad: ({ context }) => {
    // Must be logged in to register (OAuth first, then complete registration)
    if (!context.auth?.user) {
      throw redirect({ to: '/login' })
    }
  },
  component: RegisterRoute,
})

function RegisterRoute() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentUser } = useAuth()
  const {
    isRegistered,
    isError: isRegistrationCheckFailed,
    isLoading: isCheckingRegistration,
  } = useRegistrationStatus()

  // Already-registered users never see the form — completing it again would
  // create a second team. Bounce them to the app once the check resolves.
  // Only act on a DEFINITIVE answer: a transient check failure must neither
  // bounce a registered user off the form (annoying) nor let an unregistered
  // user through (dangerous). On error we hold the current view.
  useEffect(() => {
    if (!isCheckingRegistration && !isRegistrationCheckFailed && isRegistered) {
      navigate({ to: '/campaigns', replace: true })
    }
  }, [isCheckingRegistration, isRegistrationCheckFailed, isRegistered, navigate])

  const handleComplete = async () => {
    // Invalidate registration-status FIRST so /campaigns (which gates on it)
    // doesn't bounce the user back here — the query caches false for 5 min.
    queryClient.invalidateQueries({ queryKey: ['registration-status'] })
    if (currentUser?.email) {
      queryClient.invalidateQueries({ queryKey: queryKeys.profile.client(currentUser.email) })
    }
    // Invalidate preferences query to refresh data
    await queryClient.invalidateQueries({ queryKey: queryKeys.preferences.user() })
    // Navigate to campaigns after registration
    navigate({ to: '/campaigns' })
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4 sm:p-6 md:p-8 relative overflow-hidden">
      {/* Background Effects */}
      <AnimatedBackground />

      {/* my-auto keeps the card centered while letting it scroll when taller
          than the viewport (items-center clips the top in that case). */}
      <div className="relative z-10 my-auto">
        <NewUserRegistrationModal
          isOpen={true}
          userEmail={currentUser?.email || ''}
          userName={currentUser?.displayName || undefined}
          onComplete={handleComplete}
        />
      </div>
    </div>
  )
}
